/**
 * 画面上用白天图还是夜图（UI2-NIGHT-HOME-01）。天色只认后端作息（homeStage.skyOf），这里只管图什么时候换：
 * - 打开时、换房间时：直接用此刻天色那一套（浏览器照常下载，和原来一样）；
 * - 开着页面昼夜交替（同一间）：先在后台把要换上的那一整套（底图＋全部遮挡片）下好、解码好，再同一次换上——
 *   不边下边画，也不出现夜底图配白天遮挡片；
 * - 夜图有一张下不来：留在白天图，failed 为 true，home.css 用原来的深藏青压暗当夜里（等于改前的样子）；回到白天再入夜会再试；
 * - 同一间换上以后淡入一次：新图压在旧图上面淡入（fadeFrom 给出旧图是白天还是夜里，HomeScene 把旧图垫在下面），换房间不淡入。
 */
import { useEffect, useLayoutEffect, useRef, useState } from "react";
import type { HomeRoom } from "@/shared/contracts";
import { plateSources } from "./homeStage";

/** 同一间昼夜换图的淡入时长，和天色层原有的 1.2 秒渐变一致；减少动态时由全局 data-motion="enter" 换成 260ms 淡入。 */
export const NIGHT_FADE_MS = 1200;

/**
 * 解码最多等这么久：decode() 在后台标签页里会一直等到标签页回到前台（Chrome 实测，UI-2 10-13），
 * 已经下完的图过了这个时限照样换（画得出来，只是第一帧可能晚一点解码）。
 */
export const DECODE_WAIT_MS = 1000;

/** 下好并解码完才算好；decode 失败或超过 DECODE_WAIT_MS 也算好（已经下完，画得出来）；onerror 算失败。 */
function preload(src: string): Promise<boolean> {
  return new Promise((resolve) => {
    const image = new Image();
    image.onload = () => {
      if (typeof image.decode !== "function") return resolve(true);
      const timer = setTimeout(() => resolve(true), DECODE_WAIT_MS);
      const done = () => {
        clearTimeout(timer);
        resolve(true);
      };
      image.decode().then(done, done);
    };
    image.onerror = () => resolve(false);
    image.src = src;
  });
}

export function preloadAll(srcs: readonly string[]): Promise<boolean> {
  return Promise.all(srcs.map(preload)).then((all) => all.every(Boolean));
}

export type NightPlateState = {
  /** 画面上用不用夜图 */
  night: boolean;
  /** 夜里、但夜图下不来，留在白天图 */
  failed: boolean;
  /** 同一间刚换图：旧图是不是夜图（垫在新图下面，淡入完就撤）；没在淡入时为 null */
  fadeFrom: { night: boolean } | null;
};

export function useNightPlate(room: HomeRoom, wantNight: boolean): NightPlateState {
  const [view, setView] = useState({ room, night: wantNight });
  const [failed, setFailed] = useState(false);
  // 换房间：直接用此刻天色那一套（渲染时就改，不先画一帧旧的）
  if (view.room !== room) {
    setView({ room, night: wantNight });
    if (failed) setFailed(false);
  }
  const shown = view.room === room ? view.night : wantNight;
  useEffect(() => {
    if (!wantNight && failed) setFailed(false);
    if (wantNight === shown || (wantNight && failed)) return;
    let live = true;
    void preloadAll(plateSources(room, wantNight)).then((ok) => {
      if (!live) return;
      // 白天那套下不来也照样换回白天（和原来一样边下边画）；夜图下不来才留在白天图
      if (ok || !wantNight) setView({ room, night: wantNight });
      else setFailed(true);
    });
    return () => {
      live = false;
    };
  }, [room, wantNight, shown, failed]);

  const [fade, setFade] = useState<{ room: HomeRoom; fromNight: boolean } | null>(null);
  const last = useRef({ room, night: shown });
  // 布局阶段定淡入：换图那一帧上屏之前旧图已经垫在下面，不闪
  useLayoutEffect(() => {
    const was = last.current;
    last.current = { room, night: shown };
    if (was.room === room && was.night === shown) return;
    if (was.room !== room) {
      setFade(null);
      return;
    }
    setFade({ room, fromNight: was.night });
    const timer = setTimeout(() => setFade(null), NIGHT_FADE_MS);
    return () => clearTimeout(timer);
  }, [room, shown]);

  return {
    night: shown,
    failed: wantNight && failed && !shown,
    fadeFrom: fade && fade.room === room ? { night: fade.fromNight } : null,
  };
}
